import { FormElement } from '../services/api';

export type ValueSource = 'db' | 'url';

/** Reads a value from the current URL's query params by key. */
export function getUrlParamValue(key: unknown): string {
  if (!key || typeof key !== 'string' || key.trim() === '') return '';
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  return params.get(key.trim()) ?? '';
}

/**
 * Resolves whether the element's main `value` comes from the DB ({variables}) or a URL param.
 * Keeps backward compatibility with copy_section / text specific style keys.
 */
export function getElementValueSource(element: FormElement): ValueSource {
  const s = element.styles || {};
  if (element.type === 'copy_section') {
    return s.copySectionValueSource === 'url' || s.valueSource === 'url' ? 'url' : 'db';
  }
  if (element.type === 'text') {
    return s.textValueSource === 'url' || s.valueSource === 'url' ? 'url' : 'db';
  }
  return s.valueSource === 'url' ? 'url' : 'db';
}

export function getElementUrlParamKey(element: FormElement): string {
  const s = element.styles || {};
  if (element.type === 'copy_section') {
    return String(s.copySectionUrlParamKey || s.urlParamKey || '').trim();
  }
  if (element.type === 'text') {
    return String(s.textUrlParamKey || s.urlParamKey || '').trim();
  }
  return String(s.urlParamKey || '').trim();
}

/** Resolves the element's main value from URL param or DB variable substitution. */
export function resolveElementMainValue(
  element: FormElement,
  replaceVariables?: (text: string) => string,
  fallback = '',
): string {
  if (getElementValueSource(element) === 'url') {
    const key = getElementUrlParamKey(element);
    if (key) {
      const fromUrl = getUrlParamValue(key);
      // Param present (including empty string) → use it; missing → fall back so builder preview still works.
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      if (params.has(key)) return fromUrl;
    }
    // No key configured or param not in URL → fall back to saved element.value / default.
    const raw = element.value || fallback;
    return replaceVariables ? replaceVariables(raw) : raw;
  }
  const raw = element.value || fallback;
  return replaceVariables ? replaceVariables(raw) : raw;
}

/** text_with_value left-side text (styles.textValue). */
export function getSideTextValueSource(element: FormElement): ValueSource {
  return element.styles?.sideTextValueSource === 'url' ? 'url' : 'db';
}

export function resolveSideTextValue(
  element: FormElement,
  replaceVariables?: (text: string) => string,
  fallback = '',
): string {
  if (getSideTextValueSource(element) === 'url') {
    const key = String(element.styles?.sideTextUrlParamKey || '').trim();
    if (key) {
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      if (params.has(key)) return getUrlParamValue(key);
    }
    const raw = element.styles?.textValue || fallback;
    return replaceVariables ? replaceVariables(raw) : raw;
  }
  const raw = element.styles?.textValue || fallback;
  return replaceVariables ? replaceVariables(raw) : raw;
}

/** Timer expiry timestamp / countdown source. */
export function resolveTimerSourceValue(
  element: FormElement,
  transactionData: Record<string, any> | null,
  replaceVariables?: (text: string) => string,
  getVariableValue?: (data: Record<string, any> | null, key: string) => string | undefined,
): string {
  const source = element.styles?.timerTimeSource ?? 'static';
  if (source === 'url') {
    return getUrlParamValue(element.styles?.timerUrlParamKey);
  }
  if (source === 'variable') {
    const name = element.styles?.timerVariableName;
    if (!name || !getVariableValue) return '';
    return getVariableValue(transactionData, name) ?? '';
  }
  const raw = element.styles?.initialTime || element.value || '00:00';
  return replaceVariables ? replaceVariables(raw) : raw;
}
